// Mobile card list of users.
import { Trash, PencilSimple, Key } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/LanguageContext';
import { ROLE_META, fmtDate, RoleBadge, StatusPill } from './userMeta';

export const UserCardList = ({ users, currentUserId, employeeName, onToggleActive, onResetPw, onEdit, onDelete }) => {
  const { t } = useT();
  return (
    <section className="md:hidden space-y-3" data-testid="user-cards">
      {users.map((u) => {
        const isSelf = u.id === currentUserId;
        const meta = ROLE_META[u.role] || ROLE_META.user;
        return (
          <article key={u.id} data-testid={`user-card-${u.id}`} className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start gap-3">
              <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-full bg-zinc-100 text-zinc-700 text-base font-bold shadow-sm shrink-0">
                {(u.name || u.email || '?').slice(0, 1).toUpperCase()}
                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${meta.dot}`} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="font-semibold text-zinc-900 truncate">{u.name}</p>
                  {isSelf && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold shrink-0">{t('users.self')}</span>
                  )}
                </div>
                <p className="text-[11px] text-zinc-500 truncate">{u.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <RoleBadge role={u.role} />
                  <StatusPill
                    active={u.is_active}
                    disabled={isSelf}
                    onClick={() => onToggleActive(u)}
                    testid={`toggle-active-${u.id}-m`}
                    title={isSelf ? t('users.selfStateLock') : (u.is_active ? t('users.deactivate') : t('users.activate'))}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-100 grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <p className="text-zinc-400 uppercase tracking-wider font-semibold mb-0.5">{t('users.linkedEmployee')}</p>
                <p className="text-zinc-700 truncate">{u.employee_id ? employeeName(u.employee_id) : '—'}</p>
              </div>
              <div>
                <p className="text-zinc-400 uppercase tracking-wider font-semibold mb-0.5">{t('users.createdAt')}</p>
                <p className="text-zinc-700 tabular-nums">{fmtDate(u.created_at)}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={() => onResetPw(u)} data-testid={`reset-pw-${u.id}-m`} className="h-8 px-2 text-zinc-600 hover:text-teal-700" title={t('users.resetPwTitle')}>
                <Key size={14} className="mr-1" /> {t('users.reset')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onEdit(u)} data-testid={`edit-${u.id}-m`} className="h-8 px-2 text-zinc-600 hover:text-teal-700">
                <PencilSimple size={14} className="mr-1" /> {t('users.edit')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDelete(u)} data-testid={`delete-${u.id}-m`} disabled={isSelf} className="h-8 px-2 text-zinc-600 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed">
                <Trash size={14} />
              </Button>
            </div>
          </article>
        );
      })}
    </section>
  );
};
